/**
 * Knight Hauler signature weapon — a medieval flail trailing behind the
 * car. Chain + spiked iron ball that swings around the car as it drives,
 * smashing any cop / FBI it touches.
 *
 * Lives entirely on the player car when `kind === "knight"`. Runs every
 * frame (visual + collision). Per-enemy hit-cooldown so a single
 * encounter doesn't multi-tick the same cop in one frame.
 */

const CHAIN_LENGTH = 56;       // distance from car center to ball center
const BALL_RADIUS = 11;        // pixel-art spiked ball
const SWING_RATE = 0.16;       // radians per frame baseline
const SWING_SPEED_BOOST = 0.14; // additional radians/frame proportional to car speed
const BALL_DMG = 35;            // hp per hit
const HIT_COOLDOWN_MS = 700;    // per-enemy cooldown so a brushing pass doesn't melt them
const HIT_RADIUS = BALL_RADIUS + 18; // generous reach (pixel-art forgiveness)

/** Initialise flail state on the player. Idempotent. */
export function initKnightFlail(st) {
  if (st.knightFlail) return;
  st.knightFlail = {
    angle: 0,            // current swing angle around the car
    hitCooldowns: {},    // enemyIdx -> performance.now() expiry
    sparkAt: 0,          // last spark frame (for hit visuals)
  };
}

/**
 * Per-frame update — swings the ball and damages any enemy within reach.
 * Skips entirely if the player isn't driving the Knight Hauler or is
 * jailed / carjacked (st.gang state === "stolen").
 */
export function updateKnightFlail(st, sendEvent) {
  const f = st.knightFlail;
  if (!f) return;
  const p = st.player;
  if (!p) return;
  const now = performance.now();
  // Pause swings while the player is locked out (jail, carjacked).
  if (p.jailedUntil > now) return;
  if (st.gang && st.gang.state === "stolen") return;

  // Swing rate scales with car speed so it feels like inertia is
  // throwing the ball further when the player accelerates.
  const speed = Math.hypot(p.vx, p.vy);
  f.angle += SWING_RATE + Math.min(0.45, speed * 0.04) * SWING_SPEED_BOOST;
  if (f.angle > Math.PI * 2) f.angle -= Math.PI * 2;

  // Ball world position — orbits around the car. The flail trails the
  // direction the car is facing by ~180° so it looks attached behind.
  const trailingAngle = (p.angle ?? 0) + Math.PI;
  const ax = trailingAngle + Math.cos(f.angle) * 1.2; // wobble axis
  const bx = p.x + Math.cos(ax) * CHAIN_LENGTH;
  const by = p.y + Math.sin(ax) * CHAIN_LENGTH;
  f.ballX = bx;
  f.ballY = by;

  // Collision pass — damage any enemy within HIT_RADIUS not on cooldown.
  if (st.enemies) {
    for (let i = 0; i < st.enemies.length; i++) {
      if (st.destroyedEnemyIdx?.has(i)) continue;
      const e = st.enemies[i];
      if (!e) continue;
      const expiry = f.hitCooldowns[i] || 0;
      if (expiry > now) continue;
      if (Math.hypot(e.x - bx, e.y - by) > HIT_RADIUS) continue;
      // HIT — deal damage, stagger the enemy, set cooldown.
      e.hp = Math.max(0, (e.hp ?? 100) - BALL_DMG);
      e.disabled = Math.max(e.disabled || 0, now + 600);
      f.hitCooldowns[i] = now + HIT_COOLDOWN_MS;
      f.sparkAt = now;
      // Knock the enemy back away from the ball.
      const knockA = Math.atan2(e.y - by, e.x - bx);
      e.x += Math.cos(knockA) * 8;
      e.y += Math.sin(knockA) * 8;
      // If the enemy is dead, spawn a small wreckage burst and broadcast.
      if (e.hp <= 0) {
        st.destroyedEnemyIdx?.add(i);
        if (st.explosions) {
          st.explosions.push({
            x: e.x, y: e.y, age: 0, accentColor: "#cfcad4", wreckage: true,
            particles: _spark(e.x, e.y, 12, "#cfcad4"),
          });
        }
        sendEvent?.({ type: "enemy_destroyed", idx: i });
      }
    }
  }
}

function _spark(x, y, count, color) {
  const out = [];
  for (let i = 0; i < count; i++) {
    const a = Math.random() * Math.PI * 2;
    const sp = 2 + Math.random() * 4;
    out.push({
      x, y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp,
      r: 3 + Math.random() * 3, life: 22 + Math.random() * 18,
      type: "flame", hue: i % 2 === 0 ? "#FFE39A" : color,
    });
  }
  return out;
}

/** Render the flail behind the player car. */
export function drawKnightFlail(ctx, st) {
  const f = st.knightFlail;
  if (!f || f.ballX === undefined) return;
  const p = st.player;
  if (!p) return;
  const now = performance.now();

  // Chain — series of small grey links from the car to the ball.
  const cx = p.x, cy = p.y;
  const bx = f.ballX, by = f.ballY;
  const segs = 8;
  for (let i = 1; i < segs; i++) {
    const t = i / segs;
    const lx = cx + (bx - cx) * t;
    const ly = cy + (by - cy) * t;
    ctx.fillStyle = "#3a3340";
    ctx.fillRect(Math.round(lx) - 2, Math.round(ly) - 2, 4, 4);
    ctx.fillStyle = "#9c98a4";
    ctx.fillRect(Math.round(lx) - 1, Math.round(ly) - 1, 2, 2);
  }

  // Ball — chunky spiked sphere
  ctx.save();
  ctx.translate(bx, by);
  // Outer dark outline
  ctx.fillStyle = "#0e0c12";
  ctx.beginPath();
  ctx.arc(0, 0, BALL_RADIUS + 1, 0, Math.PI * 2);
  ctx.fill();
  // Iron core
  ctx.fillStyle = "#5C5762";
  ctx.beginPath();
  ctx.arc(0, 0, BALL_RADIUS, 0, Math.PI * 2);
  ctx.fill();
  // Highlight
  ctx.fillStyle = "#9c98a4";
  ctx.beginPath();
  ctx.arc(-BALL_RADIUS * 0.35, -BALL_RADIUS * 0.35, BALL_RADIUS * 0.45, 0, Math.PI * 2);
  ctx.fill();
  // Spikes — 8 chunky pixel triangles around the ball
  const spikes = 8;
  for (let i = 0; i < spikes; i++) {
    const a = (i / spikes) * Math.PI * 2 + f.angle * 0.5;
    const tipX = Math.cos(a) * (BALL_RADIUS + 6);
    const tipY = Math.sin(a) * (BALL_RADIUS + 6);
    const baseAx = Math.cos(a + 0.3) * BALL_RADIUS;
    const baseAy = Math.sin(a + 0.3) * BALL_RADIUS;
    const baseBx = Math.cos(a - 0.3) * BALL_RADIUS;
    const baseBy = Math.sin(a - 0.3) * BALL_RADIUS;
    ctx.strokeStyle = "#0e0c12";
    ctx.lineWidth = 1;
    ctx.fillStyle = "#cfcad4";
    ctx.beginPath();
    ctx.moveTo(tipX, tipY);
    ctx.lineTo(baseAx, baseAy);
    ctx.lineTo(baseBx, baseBy);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  }
  ctx.restore();

  // Recent-hit spark — yellow flash on the ball
  if (now - (f.sparkAt || 0) < 180) {
    const a = (180 - (now - f.sparkAt)) / 180;
    ctx.save();
    ctx.globalAlpha = a;
    ctx.fillStyle = "#FFE39A";
    ctx.beginPath();
    ctx.arc(bx, by, BALL_RADIUS + 8, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
}
